import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import translations from '../i18n/translations';
import { userService } from '../services/userService';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [isLoading, setIsLoading] = useState(true);
  const debounceTimeoutRef = useRef(null);
  const lastUserIdRef = useRef(null);

  // Load language from localStorage on mount (without auth)
  useEffect(() => {
    initializeLanguageFromStorage();

    // Cleanup function to clear timeout on unmount
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const initializeLanguageFromStorage = () => {
    setIsLoading(true);
    const savedLanguage = localStorage.getItem('app_language');
    if (savedLanguage && translations[savedLanguage]) {
      setCurrentLanguage(savedLanguage);
    } else {
      // Detect browser language
      const browserLang = navigator.language.split('-')[0];
      if (translations[browserLang]) {
        setCurrentLanguage(browserLang);
      }
    }
    setIsLoading(false);
  };

  // Debounced function to initialize language with user data
  const initializeWithUser = async (user, isAuthenticated) => {
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // If same user, don't make another request
    if (lastUserIdRef.current === user?.id) {
      return;
    }

    // Debounce the API call
    debounceTimeoutRef.current = setTimeout(async () => {
      if (isAuthenticated && user?.id) {
        lastUserIdRef.current = user.id;

        try {
          // Try to get language from user settings
          const userSettings = await userService.getUserSettings(user.id);
          const userLanguage = userSettings.settings?.language;

          if (userLanguage && translations[userLanguage]) {
            setCurrentLanguage(userLanguage);
            // Also save to localStorage as backup
            localStorage.setItem('app_language', userLanguage);
            return;
          }
        } catch (error) {
          console.warn('Failed to load user language settings:', error);
        }
      }

      // If no user language found, check if we need to save current language to user settings
      if (isAuthenticated && user?.id && currentLanguage) {
        try {
          await userService.updateUserSettings(user.id, { language: currentLanguage });
        } catch (error) {
          console.warn('Failed to save current language to user settings:', error);
        }
      }
    }, 300); // 300ms debounce delay
  };

  const changeLanguage = async (languageCode, user = null, isAuthenticated = false) => {
    if (!translations[languageCode]) {
      console.warn(`Language ${languageCode} not available`);
      return;
    }

    setCurrentLanguage(languageCode);

    if (isAuthenticated && user?.id) {
      try {
        // Save to user settings
        await userService.updateUserSettings(user.id, { language: languageCode });
      } catch (error) {
        console.error('Failed to save language to user settings:', error);
      }
    }

    // Always save to localStorage as backup
    localStorage.setItem('app_language', languageCode);
  };

  // Translation function
  const t = (key, params = {}) => {
    const keys = key.split('.');
    let value = translations[currentLanguage];

    for (const k of keys) {
      value = value?.[k];
    }

    // Fallback to English if translation not found
    if (!value) {
      let fallback = translations.en;
      for (const k of keys) {
        fallback = fallback?.[k];
      }
      value = fallback || key;
    }

    // Simple parameter replacement
    if (typeof value === 'string' && Object.keys(params).length > 0) {
      return value.replace(/\{\{(\w+)\}\}/g, (match, param) => params[param] || match);
    }

    return value || key;
  };

  const getAvailableLanguages = () => {
    return [
      { code: 'en', name: 'English', nativeName: 'English' },
      { code: 'fr', name: 'French', nativeName: 'Français' }
    ];
  };

  const getCurrentLanguageInfo = () => {
    const languages = getAvailableLanguages();
    return languages.find(lang => lang.code === currentLanguage) || languages[0];
  };

  const value = {
    currentLanguage,
    changeLanguage,
    initializeWithUser,
    t,
    getAvailableLanguages,
    getCurrentLanguageInfo,
    isLoading
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};